import { useCallback } from 'react';
import { Link } from 'react-router';
import { CopyPageButton } from '@/components/academy/CopyPageButton';
import {
  Rocket,
  Chrome,
  Mail,
  Search,
  Key,
  CreditCard,
  HelpCircle,
  ArrowRight,
  Upload,
  PenLine,
  FileText,
  Send,
  Globe,
  Clock,
  Shield,
  MessageCircleQuestion,
  Camera,
  Play,
  Sparkles,
  MonitorSmartphone,
  Puzzle,
  Inbox,
  CheckCircle2,
  Circle,
  Zap,
  Database,
  Brain,
  MailPlus,
} from 'lucide-react';

/* ════════════════════════════════════════════════════════════════
   Data
   ════════════════════════════════════════════════════════════════ */

const sections = [
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'chrome-extension', label: 'Chrome Extension' },
  { id: 'outreach-workflow', label: 'Outreach Workflow' },
  { id: 'ai-research', label: 'AI Research' },
  { id: 'api-keys', label: 'API Keys' },
  { id: 'billing', label: 'Billing & Usage' },
  { id: 'faq', label: 'FAQ' },
];

const faqItems = [
  {
    q: 'The Chrome extension shows "Not Open"',
    a: 'Make sure the HyPeMa web app is open in a browser tab. The extension communicates with the web app via the browser. Try refreshing both the web app and the extension popup.',
  },
  {
    q: 'I ran out of tokens',
    a: 'Upgrade to Pro in Settings \u2192 Billing for 4,900 tokens/month. Free tier (500 tokens) resets monthly.',
  },
  {
    q: 'Email generation is taking very long',
    a: 'AI research with web search can take 1\u20135 minutes for complex companies. This is normal. Don\u2019t refresh the page \u2014 the operation continues in the background.',
  },
  {
    q: 'I get "Rate limited" errors',
    a: 'HyPeMa limits concurrent AI operations to prevent overload. Wait for your current operations to finish before starting new ones.',
  },
  {
    q: 'Can I use HyPeMa with Outlook instead of Gmail?',
    a: 'Currently only Gmail is supported via the Chrome extension. Outlook support is planned for a future release.',
  },
  {
    q: 'How accurate are the AI-generated leads?',
    a: 'Leads are found via real-time web search. Email addresses are sourced from public websites or constructed from company domain patterns. Some emails may bounce. Always review leads before sending.',
  },
];

/* ════════════════════════════════════════════════════════════════
   Page Component
   ════════════════════════════════════════════════════════════════ */

export function AcademyGuidePage() {
  const smoothScroll = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const id = e.currentTarget.getAttribute('href')?.replace('#', '');
      if (!id) return;
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [],
  );

  return (
    <div className="animate-fade-in flex gap-8">
      {/* Sticky ToC */}
      <aside className="hidden lg:block w-48 shrink-0">
        <nav className="sticky top-6 space-y-1">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={smoothScroll}
              className="block text-sm text-base-content/50 hover:text-base-content py-1 transition-colors"
            >
              {s.label}
            </a>
          ))}
        </nav>
      </aside>

      <div id="guide-content" className="flex-1 min-w-0 space-y-12 max-w-3xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Web App Guide</h1>
            <p className="text-sm text-base-content/40 mt-1">
              Everything you need to know about using HyPeMa for hyper-personalized outreach.
            </p>
          </div>
          <CopyPageButton contentSelector="#guide-content" title="HyPeMa Web App Guide" />
        </div>

        {/* ──────────────────────────────────────────────────────────
            1. Getting Started
           ────────────────────────────────────────────────────────── */}
        <section id="getting-started" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Rocket} title="Getting Started" />

          <div className="glass-card rounded-xl p-5 space-y-5">
            <p className="text-sm text-base-content/70 leading-relaxed">
              HyPeMa turns cold outreach into a simple 3-step process. AI finds your leads,
              writes personalized emails for each one, and puts them in your Gmail as drafts
              &mdash; you just review and hit send.
            </p>

            <div className="glass-card rounded-xl p-4 border-l-4 border-primary/60">
              <p className="text-sm font-medium text-primary/90">Why it works</p>
              <p className="text-sm text-base-content/60 mt-1">
                Every email is personalized using AI and real data about each lead. No templates,
                no spray-and-pray. Each recipient gets a unique message that references their
                company, role, and context.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                How it works
              </p>
              <ol className="space-y-2 text-sm text-base-content/70">
                <StepItem number={1}>
                  <span className="font-medium">Find leads</span> &mdash; AI searches the web
                  for real people in your target industry and region
                </StepItem>
                <StepItem number={2}>
                  <span className="font-medium">Draft emails</span> &mdash; describe your pitch
                  and AI writes a unique, personalized email for every lead
                </StepItem>
                <StepItem number={3}>
                  <span className="font-medium">Review &amp; send</span> &mdash; emails appear as
                  Gmail drafts. Review each one and send when ready.
                </StepItem>
              </ol>
            </div>

            <p className="text-sm text-base-content/50 italic">
              During onboarding you entered your company website URL (or details manually).
              That context is what AI uses to personalize every email for your specific product
              and audience. You can update it anytime in Settings.
            </p>
          </div>

          <VideoPlaceholder caption="Watch: Your first outreach campaign — from leads to Gmail drafts (2 min)" />
        </section>

        {/* ──────────────────────────────────────────────────────────
            2. Chrome Extension
           ────────────────────────────────────────────────────────── */}
        <section id="chrome-extension" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Chrome} title="Chrome Extension" />

          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm text-base-content/70 leading-relaxed">
              The Chrome extension creates Gmail drafts directly in your inbox. HyPeMa generates
              the email content; the extension places it in your Gmail as a draft for you to review
              and send.
            </p>

            {/* Architecture Diagram */}
            <ArchitectureDiagram />

            <div className="space-y-3">
              <InfoRow icon={Globe} title="Installation">
                Load the extension unpacked in Chrome via chrome://extensions (developer mode).
                See Settings for the install link and instructions.
              </InfoRow>

              <InfoRow icon={Shield} title="Why drafts, not direct send?">
                You maintain full control. Review each email in Gmail before sending. No
                deliverability issues since emails come from your real account.
              </InfoRow>

              <InfoRow icon={HelpCircle} title="Troubleshooting">
                If the extension popup shows &ldquo;Not Open&rdquo;, make sure the HyPeMa web app
                is open in a browser tab. The extension needs it to communicate.
              </InfoRow>
            </div>
          </div>

          <VideoPlaceholder caption="Watch: How the Chrome extension creates Gmail drafts in real-time" />
        </section>

        {/* ──────────────────────────────────────────────────────────
            3. Outreach Workflow
           ────────────────────────────────────────────────────────── */}
        <section id="outreach-workflow" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Mail} title="Outreach Workflow" />

          <p className="text-sm text-base-content/60 leading-relaxed">
            The entire outreach process is just <span className="font-semibold text-base-content/80">three steps</span>.
            No complex setup, no integrations to configure &mdash; pick your audience, let AI write
            the emails, and send through Gmail.
          </p>

          {/* Flow Diagram */}
          <FlowDiagram />

          <VideoPlaceholder caption="Watch: Full outreach flow — from finding leads to Gmail drafts (3 min)" />

          {/* Step 1 */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <StepHeader number={1} title="Find Leads" />

            <ul className="space-y-2.5 text-sm text-base-content/70 ml-8">
              <li className="flex items-start gap-2">
                <Sparkles size={14} className="mt-0.5 shrink-0 text-primary/60" />
                <span>
                  <span className="font-medium">AI lead generation:</span> Enter an industry, role,
                  and region &mdash; AI searches the web for real people and their contact details.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Upload size={14} className="mt-0.5 shrink-0 text-primary/60" />
                <span>
                  <span className="font-medium">Import from CSV:</span> Upload a CSV with existing
                  contacts &mdash; AI auto-maps columns.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <PenLine size={14} className="mt-0.5 shrink-0 text-primary/60" />
                <span>
                  <span className="font-medium">Manual entry:</span> Add leads one at a time.
                </span>
              </li>
            </ul>
            <p className="text-xs text-base-content/40 ml-8">
              Leads are organized into groups (e.g., &ldquo;Munich Accountants&rdquo;,
              &ldquo;Chicago Lawyers&rdquo;).
            </p>

            <ScreenshotPlaceholder caption="Find Leads page — AI generation modal and lead group cards" />
          </div>

          {/* Step 2 */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <StepHeader number={2} title="Draft Emails" />

            <ol className="space-y-2 text-sm text-base-content/70 ml-8 list-decimal list-inside">
              <li>Select a lead group</li>
              <li>
                Describe your pitch/goal in the &ldquo;Topic &amp; Goal&rdquo; field &mdash; this
                is what the AI uses to personalize
              </li>
              <li>Preview sample emails and give feedback to refine the style</li>
              <li>Generate emails for all leads in the group (batch generation)</li>
            </ol>
            <p className="text-xs text-base-content/40 ml-8">
              Each email is unique &mdash; AI uses the lead&rsquo;s company, role, and context
              to craft a personalized message.
            </p>

            <ScreenshotPlaceholder caption="Draft Emails page — batch generation with AI-powered personalization" />
          </div>

          {/* Step 3 */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <StepHeader number={3} title="Review & Send" />

            <ol className="space-y-2 text-sm text-base-content/70 ml-8 list-decimal list-inside">
              <li>Review all generated emails</li>
              <li>Use the Chrome extension to create Gmail drafts</li>
              <li>Open Gmail, review each draft, and send when ready</li>
            </ol>

            <StatusPipeline />

            <ScreenshotPlaceholder caption="Review & Send page — email list with Gmail draft creation" />
          </div>
        </section>

        {/* ──────────────────────────────────────────────────────────
            4. AI Company Research
           ────────────────────────────────────────────────────────── */}
        <section id="ai-research" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Search} title="AI Company Research" />

          <div className="glass-card rounded-xl p-5 space-y-5">
            <p className="text-sm text-base-content/70 leading-relaxed">
              When you enter a company URL, AI uses web search to find detailed information about
              the company. This data powers the email personalization &mdash; each email references
              specific details about the recipient&rsquo;s company.
            </p>

            {/* AI Research Flow */}
            <AiResearchFlow />

            <div className="flex items-start gap-3 text-sm text-base-content/60">
              <Clock size={16} className="mt-0.5 shrink-0 text-warning/70" />
              <div>
                <p className="font-medium text-base-content/70">Processing time</p>
                <p>
                  30 seconds to 5 minutes, depending on how much information is available online.
                  You&rsquo;ll see live status updates as the AI searches.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ──────────────────────────────────────────────────────────
            5. API Keys
           ────────────────────────────────────────────────────────── */}
        <section id="api-keys" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Key} title="API Keys" />

          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm text-base-content/70 leading-relaxed">
              Access the HyPeMa API programmatically &mdash; build integrations, automate outreach,
              and connect to other tools.
            </p>

            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Creating a Key
              </p>
              <ol className="space-y-1 text-sm text-base-content/70 list-decimal list-inside">
                <li>Go to <span className="font-medium">Settings &rarr; API Keys</span></li>
                <li>Click <span className="font-medium">Create API Key</span> and give it a name</li>
                <li>Copy the key &mdash; it is shown only once</li>
              </ol>
            </div>

            <div className="glass-card rounded-xl p-4 border-l-4 border-warning/60 space-y-2">
              <p className="text-sm font-medium text-warning/90 flex items-center gap-2">
                <Shield size={14} />
                Security
              </p>
              <ul className="text-sm text-base-content/60 space-y-1 list-disc list-inside">
                <li>
                  Keys start with{' '}
                  <code className="text-xs bg-base-200/60 rounded px-1.5 py-0.5">hpm_live_</code>{' '}
                  and are hashed in the database
                </li>
                <li>Never share your key &mdash; revoke it immediately if compromised</li>
              </ul>
            </div>

            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Usage
              </p>
              <div className="bg-base-200/40 rounded-lg px-4 py-3">
                <code className="text-xs text-base-content/70 break-all">
                  Authorization: Bearer hpm_live_your_key
                </code>
              </div>
            </div>

            <p className="text-sm text-base-content/50">
              Full API documentation is available on the{' '}
              <Link to="/academy/api-reference" className="text-primary hover:underline font-medium">
                API Reference
              </Link>{' '}
              page.
            </p>
          </div>

          <ScreenshotPlaceholder caption="API Keys settings — create, view, and revoke your API keys" />
        </section>

        {/* ──────────────────────────────────────────────────────────
            6. Billing & Usage
           ────────────────────────────────────────────────────────── */}
        <section id="billing" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={CreditCard} title="Billing & Usage" />

          <div className="glass-card rounded-xl p-5 space-y-5">
            {/* Plans */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Plans
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <PlanCard name="Free" tokens="500" price="Free" detail="Try out HyPeMa" />
                <PlanCard
                  name="Pro"
                  tokens="4,900"
                  price={'\u20AC49/mo'}
                  detail="For serious outreach campaigns"
                  highlighted
                />
              </div>
            </div>

            {/* Token Cost Chart */}
            <TokenCostChart />

            <p className="text-xs text-base-content/40 italic">
              Only AI-powered operations cost tokens. Managing your leads, groups, and settings
              is always free &mdash; tokens are only consumed when AI does work for you.
            </p>

            <div className="space-y-1 text-sm text-base-content/60">
              <p>
                <span className="font-medium text-base-content/70">Check usage:</span> Settings
                &rarr; Usage &mdash; view token consumption, daily breakdown, and operation history.
              </p>
              <p>
                <span className="font-medium text-base-content/70">Upgrade:</span> Settings &rarr;
                Billing &rarr; select a plan.
              </p>
            </div>
          </div>

          <ScreenshotPlaceholder caption="Usage dashboard — token consumption, daily chart, and operation history" />
        </section>

        {/* ──────────────────────────────────────────────────────────
            7. FAQ & Troubleshooting
           ────────────────────────────────────────────────────────── */}
        <section id="faq" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={HelpCircle} title="FAQ & Troubleshooting" />

          <div className="space-y-3">
            {faqItems.map((item) => (
              <div key={item.q} className="glass-card rounded-xl p-5 space-y-2">
                <p className="text-sm font-medium flex items-start gap-2">
                  <MessageCircleQuestion size={16} className="mt-0.5 shrink-0 text-primary/70" />
                  {item.q}
                </p>
                <p className="text-sm text-base-content/60 ml-6">{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Helper Components — Text
   ════════════════════════════════════════════════════════════════ */

function SectionHeading({ icon: Icon, title }: { icon: React.ElementType; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={20} className="text-primary" />
      <h2 className="text-lg font-semibold">{title}</h2>
    </div>
  );
}

function StepItem({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/15 text-primary text-xs font-bold shrink-0 mt-0.5">
        {number}
      </span>
      <span>{children}</span>
    </li>
  );
}

function StepHeader({ number, title }: { number: number; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/15 text-primary text-sm font-bold">
        {number}
      </span>
      <h3 className="text-sm font-semibold">{title}</h3>
    </div>
  );
}

function InfoRow({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon size={15} className="mt-0.5 shrink-0 text-primary/60" />
      <div>
        <p className="font-medium text-base-content/80">{title}</p>
        <p className="text-base-content/60">{children}</p>
      </div>
    </div>
  );
}

function PlanCard({
  name,
  tokens,
  price,
  detail,
  highlighted,
}: {
  name: string;
  tokens: string;
  price: string;
  detail: string;
  highlighted?: boolean;
}) {
  return (
    <div
      className={`rounded-lg p-4 text-center space-y-1 ${
        highlighted ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-base-200/40'
      }`}
    >
      <p className="text-sm font-semibold">{name}</p>
      <p className="text-lg font-bold text-primary">{tokens}</p>
      <p className="text-xs text-base-content/50">tokens/month</p>
      <p className="text-xs font-medium text-base-content/70">{price}</p>
      <p className="text-xs text-base-content/40">{detail}</p>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Helper Components — Placeholders
   ════════════════════════════════════════════════════════════════ */

function ScreenshotPlaceholder({ caption }: { caption: string }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-base-content/10 bg-base-200/20 p-8 flex flex-col items-center justify-center gap-3 text-center">
      <Camera size={28} className="text-base-content/20" />
      <p className="text-xs text-base-content/30 max-w-sm">{caption}</p>
    </div>
  );
}

function VideoPlaceholder({ caption }: { caption: string }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-primary/20 bg-primary/[0.03] aspect-video max-h-56 flex flex-col items-center justify-center gap-3 text-center px-6">
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
        <Play size={20} className="text-primary/50 ml-0.5" />
      </div>
      <p className="text-xs text-primary/40 max-w-sm">{caption}</p>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Helper Components — Diagrams
   ════════════════════════════════════════════════════════════════ */

/** 3-step outreach flow diagram */
function FlowDiagram() {
  const steps = [
    { icon: Search, title: 'Find Leads', desc: 'AI search, CSV, manual' },
    { icon: MailPlus, title: 'Draft Emails', desc: 'Personalize with AI' },
    { icon: Send, title: 'Review & Send', desc: 'Create Gmail drafts' },
  ];

  return (
    <div className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-0">
      {steps.map((step, i) => (
        <div key={step.title} className="flex items-center flex-1 min-w-0">
          <div className="flex-1 glass-card rounded-xl p-4 text-center space-y-1.5">
            <step.icon size={22} className="mx-auto text-primary" />
            <p className="text-sm font-semibold">{step.title}</p>
            <p className="text-xs text-base-content/40">{step.desc}</p>
          </div>
          {i < steps.length - 1 && (
            <div className="hidden sm:flex items-center px-2 text-base-content/20">
              <ArrowRight size={18} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Chrome extension architecture diagram */
function ArchitectureDiagram() {
  const boxes = [
    { icon: MonitorSmartphone, title: 'HyPeMa', sub: 'Web App', color: 'primary' },
    { icon: Puzzle, title: 'Chrome', sub: 'Extension', color: 'secondary' },
    { icon: Inbox, title: 'Gmail', sub: 'Your Drafts', color: 'accent' },
  ];
  const arrows = ['postMessage', 'opens compose'];

  return (
    <div className="rounded-xl bg-base-200/30 p-5">
      <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-0">
        {boxes.map((box, i) => (
          <div key={box.title} className="flex items-center flex-1 min-w-0 w-full sm:w-auto">
            <div className="flex-1 rounded-lg border border-base-content/10 bg-base-100 p-3 text-center space-y-1">
              <box.icon size={20} className={`mx-auto text-${box.color}`} />
              <p className="text-xs font-semibold">{box.title}</p>
              <p className="text-[10px] text-base-content/40">{box.sub}</p>
            </div>
            {i < boxes.length - 1 && (
              <div className="hidden sm:flex flex-col items-center px-2 gap-0.5">
                <ArrowRight size={14} className="text-base-content/25" />
                <span className="text-[9px] text-base-content/25 whitespace-nowrap">
                  {arrows[i]}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Lead status pipeline: new → generated → drafted → sent */
function StatusPipeline() {
  const statuses = [
    { label: 'new', color: 'bg-base-content/20' },
    { label: 'generated', color: 'bg-blue-400' },
    { label: 'drafted', color: 'bg-amber-400' },
    { label: 'sent', color: 'bg-emerald-400' },
  ];

  return (
    <div className="ml-8">
      <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40 mb-2">
        Lead Status Flow
      </p>
      <div className="flex items-center gap-1">
        {statuses.map((s, i) => (
          <div key={s.label} className="flex items-center gap-1">
            <div className="flex flex-col items-center gap-1">
              <div className={`w-3 h-3 rounded-full ${s.color}`} />
              <span className="text-[10px] text-base-content/40">{s.label}</span>
            </div>
            {i < statuses.length - 1 && (
              <div className="w-6 sm:w-10 h-px bg-base-content/15 mb-4" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** AI Research: URL → Web Search → Structured Data → Personalization */
function AiResearchFlow() {
  const steps = [
    { icon: Globe, label: 'Enter company URL', detail: 'e.g. https://example.com' },
    { icon: Brain, label: 'AI web search', detail: '30s \u2013 5 min' },
    { icon: Database, label: 'Structured data', detail: 'Name, industry, CEO, product...' },
    { icon: MailPlus, label: 'Email personalization', detail: 'Powers every outreach email' },
  ];

  return (
    <div className="space-y-0">
      {steps.map((step, i) => (
        <div key={step.label} className="flex items-start gap-3">
          {/* Vertical line + dot */}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <step.icon size={14} className="text-primary" />
            </div>
            {i < steps.length - 1 && <div className="w-px h-6 bg-primary/15" />}
          </div>
          {/* Content */}
          <div className="pt-1 pb-3">
            <p className="text-sm font-medium text-base-content/80">{step.label}</p>
            <p className="text-xs text-base-content/40">{step.detail}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Token cost comparison chart */
function TokenCostChart() {
  const ops = [
    { label: 'Company Research', cost: '5\u201315', pct: 50, paid: true },
    { label: 'Lead Generation', cost: '10\u201330', pct: 100, paid: true },
    { label: 'Email Generation', cost: '2\u20135', pct: 17, paid: true },
    { label: 'Managing Leads', cost: 'Free', pct: 0, paid: false },
  ];

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
        Token Costs by Operation
      </p>
      <div className="space-y-2.5">
        {ops.map((op) => (
          <div key={op.label} className="flex items-center gap-3">
            <span className="text-xs text-base-content/60 w-32 shrink-0 text-right">
              {op.label}
            </span>
            <div className="flex-1 h-4 rounded-full bg-base-200/50 overflow-hidden">
              {op.paid && (
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary/60 to-primary/30"
                  style={{ width: `${Math.max(op.pct, 8)}%` }}
                />
              )}
            </div>
            <span className="text-xs font-medium text-base-content/50 w-20 shrink-0">
              {op.paid ? (
                <>{op.cost} <span className="text-base-content/30">tokens</span></>
              ) : (
                <span className="flex items-center gap-1 text-emerald-500">
                  <CheckCircle2 size={11} /> Free
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
